/**
 * Quota, persistência e uso de armazenamento (PLAN §19).
 *
 * Objetivo declarado do plano: **evitar surpresa antes de uma viagem**. O usuário
 * precisa ver quanto ocupou, quanto sobra, e saber se o sistema pode apagar tudo.
 */
import { db } from '@/db/schema';
import { getFileStore } from '@/storage/index';
import { removeLocalMedia } from '@/services/media/manualImport';
import { mediaWriter } from '@/storage/index';
import type { OfflineFileStore } from '@/storage/types';

export interface StorageEstimate {
  /** `null` quando o navegador não expõe a API (F13-T07). */
  usageBytes: number | null;
  quotaBytes: number | null;
  availableBytes: number | null;
  /** Bytes ocupados pelos vídeos, medidos no próprio armazenamento de arquivos. */
  mediaBytes: number;
  /** Estimativa do que o app usa fora de mídia (catálogo, estado, shell). */
  appBytes: number | null;
  videosStored: number;
  /** `null` quando a API de persistência não existe. */
  persisted: boolean | null;
}

export function isStorageApiAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.storage?.estimate === 'function';
}

export async function readStorageEstimate(
  store: OfflineFileStore = getFileStore(),
): Promise<StorageEstimate> {
  const [mediaBytes, videosStored] = await Promise.all([
    store.totalSize().catch(() => 0),
    store.list().then((keys) => keys.length).catch(() => 0),
  ]);

  if (!isStorageApiAvailable()) {
    return {
      usageBytes: null,
      quotaBytes: null,
      availableBytes: null,
      mediaBytes,
      appBytes: null,
      videosStored,
      persisted: null,
    };
  }

  const estimate = await navigator.storage.estimate().catch(() => null);
  const persisted =
    typeof navigator.storage.persisted === 'function'
      ? await navigator.storage.persisted().catch(() => null)
      : null;

  const usageBytes = estimate?.usage ?? null;
  const quotaBytes = estimate?.quota ?? null;

  return {
    usageBytes,
    quotaBytes,
    availableBytes:
      usageBytes !== null && quotaBytes !== null ? Math.max(0, quotaBytes - usageBytes) : null,
    mediaBytes,
    // O uso total inclui a mídia; o resto é catálogo, estado pessoal e shell.
    appBytes: usageBytes !== null ? Math.max(0, usageBytes - mediaBytes) : null,
    videosStored,
    persisted,
  };
}

/**
 * Pede armazenamento persistente (F13-T05).
 *
 * Sem persistência concedida, o sistema pode apagar tudo sob pressão de espaço — e
 * é o que transforma "baixei 300 vídeos" em "não tenho nada" no meio da viagem.
 * O navegador decide sozinho; não há como forçar.
 */
export async function requestPersistence(): Promise<boolean | null> {
  if (typeof navigator === 'undefined' || typeof navigator.storage?.persist !== 'function') {
    return null;
  }

  try {
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

export interface SpaceCheck {
  allowed: boolean;
  /** Bytes estimados para a operação, quando dá para estimar. */
  requiredBytes: number | null;
  availableBytes: number | null;
  reason?: string;
}

/** Margem de segurança: nunca encher o armazenamento até a borda. */
const SAFETY_MARGIN = 1.15;

/**
 * Decide se um download em lote pode começar (F13-T08).
 *
 * Quando não há como estimar tamanho ou espaço, **permite** e deixa o erro de cota
 * ser tratado item a item. Bloquear por falta de informação impediria o uso em
 * navegadores que simplesmente não expõem a API.
 */
export function checkSpaceFor(
  requiredBytes: number | null,
  availableBytes: number | null,
): SpaceCheck {
  if (requiredBytes === null || availableBytes === null) {
    return { allowed: true, requiredBytes, availableBytes };
  }

  const needed = requiredBytes * SAFETY_MARGIN;
  if (needed <= availableBytes) {
    return { allowed: true, requiredBytes, availableBytes };
  }

  return {
    allowed: false,
    requiredBytes,
    availableBytes,
    reason:
      'O espaço disponível não é suficiente para baixar tudo. ' +
      'Libere espaço no dispositivo ou baixe por categoria.',
  };
}

export interface CategoryUsage {
  categoryId: string;
  categoryName: string;
  videos: number;
  bytes: number;
}

/** Uso por categoria (F13-T11). */
export async function usageByCategory(
  store: OfflineFileStore = getFileStore(),
): Promise<CategoryUsage[]> {
  const keys = await store.list();
  if (keys.length === 0) return [];

  const signs = await db.signs.bulkGet(keys);
  const categories = await db.categories.toArray();
  const names = new Map(categories.map((category) => [category.id, category.name]));

  const usage = new Map<string, CategoryUsage>();

  for (const [index, key] of keys.entries()) {
    const sign = signs[index];
    const categoryId = sign?.categoryId ?? 'sem-categoria';
    const size = (await store.getSize(key)) ?? 0;

    const current = usage.get(categoryId) ?? {
      categoryId,
      categoryName: names.get(categoryId) ?? 'Fora do glossário',
      videos: 0,
      bytes: 0,
    };
    current.videos += 1;
    current.bytes += size;
    usage.set(categoryId, current);
  }

  return [...usage.values()].sort((a, b) => b.bytes - a.bytes);
}

export interface RemovalResult {
  removed: number;
  freedBytes: number;
}

/**
 * Remove todos os vídeos (F13-T13).
 *
 * Apaga **apenas** arquivos e o bloco `offline` de cada estado. Favoritos, progresso
 * e histórico permanecem intactos (F13-T14) — são dados que só existem aqui e que o
 * usuário não pediu para apagar.
 */
export async function removeAllVideos(
  store: OfflineFileStore = getFileStore(),
): Promise<RemovalResult> {
  const keys = await store.list();
  const writer = mediaWriter(store);

  let freedBytes = 0;
  for (const key of keys) {
    freedBytes += (await store.getSize(key)) ?? 0;
    await removeLocalMedia(key, writer);
  }

  await store.clearTemp().catch(() => 0);

  return { removed: keys.length, freedBytes };
}

/** Remove os vídeos de uma categoria. */
export async function removeCategoryVideos(
  categoryId: string,
  store: OfflineFileStore = getFileStore(),
): Promise<RemovalResult> {
  const keys = await store.list();
  const signs = await db.signs.bulkGet(keys);
  const writer = mediaWriter(store);

  let removed = 0;
  let freedBytes = 0;

  for (const [index, key] of keys.entries()) {
    if (signs[index]?.categoryId !== categoryId) continue;
    freedBytes += (await store.getSize(key)) ?? 0;
    await removeLocalMedia(key, writer);
    removed += 1;
  }

  return { removed, freedBytes };
}
